<template>
  <nav class="smart-navbar" :class="{ 'hidden': !isVisible && !isMenuOpen, 'top': isAtTop && !isMenuOpen }">
    <div class="nav-content">
      
      <!-- LOGO -->
      <a href="#" class="logo-link" @click.prevent="scrollTo('app')">
        <img src="@/assets/logo.png" alt="Logo QB" class="logo-img" />
      </a>
      
      <!-- MENU DESKTOP -->
      <div class="nav-desktop">
        <div class="nav-links">
          <a href="#about" @click.prevent="scrollTo('about-me')">{{ t('TheNavbar.about') }}</a>
          <a href="#projets" @click.prevent="scrollTo('projets')">{{ t('TheNavbar.projects') }}</a>
          <a href="#skills" @click.prevent="scrollTo('skills')">{{ t('TheNavbar.skills') }}</a>
          <a href="#contact" @click.prevent="scrollTo('contact')">{{ t('TheNavbar.contact') }}</a>
        </div>

        <!-- DROPDOWN LANGUE (Desktop) -->
        <div class="lang-dropdown" @click="toggleLangMenu" v-click-outside="closeLangMenu">
          <button class="lang-btn-current">
            <img :src="currentFlag" alt="Langue" class="flag-icon" />
            <span class="arrow-down" :class="{ rotated: isLangOpen }">▼</span>
          </button>
          
          <transition name="fade">
            <div v-if="isLangOpen" class="lang-menu">
              <button @click.stop="switchLang('fr')" class="lang-option" :class="{ active: locale === 'fr' }">
                <img src="@/assets/flags/fr.webp" class="flag-icon" /> Français
              </button>
              <button @click.stop="switchLang('en')" class="lang-option" :class="{ active: locale === 'en' }">
                <img src="@/assets/flags/en.webp" class="flag-icon" /> English
              </button>
            </div>
          </transition>
        </div>
      </div>

      <!-- BOUTON BURGER (Mobile) -->
      <button class="burger-btn" @click="toggleMenu" :class="{ open: isMenuOpen }" aria-label="Menu">
        <span class="burger-line"></span>
        <span class="burger-line"></span>
        <span class="burger-line"></span>
      </button>

    </div>

    <!-- MENU MOBILE (Overlay) -->
    <transition name="slide-down">
      <div v-if="isMenuOpen" class="mobile-menu">
        <div class="mobile-links">
          <a href="#about" @click.prevent="scrollTo('about-me')">{{ t('TheNavbar.about') }}</a>
          <a href="#projets" @click.prevent="scrollTo('projets')">{{ t('TheNavbar.projects') }}</a>
          <a href="#skills" @click.prevent="scrollTo('skills')">{{ t('TheNavbar.skills') }}</a>
          <a href="#contact" @click.prevent="scrollTo('contact')">{{ t('TheNavbar.contact') }}</a>
        </div>

        <!-- SÉLECTEUR LANGUE MOBILE (Bien visible) -->
        <div class="mobile-lang">
          <span class="lang-label">{{ t('TheNavbar.language') }} :</span>
          <div class="lang-options-mobile">
            <button @click="switchLang('fr')" :class="{ active: locale === 'fr' }">
              <img src="@/assets/flags/fr.webp" class="flag-icon-large" /> 
              <span>FR</span>
            </button>
            <button @click="switchLang('en')" :class="{ active: locale === 'en' }">
              <img src="@/assets/flags/en.webp" class="flag-icon-large" /> 
              <span>EN</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

// Import des images
import frFlag from '@/assets/flags/fr.webp'
import enFlag from '@/assets/flags/en.webp'

const { t, locale } = useI18n() 
const isVisible = ref(true)
const isAtTop = ref(true)
const isLangOpen = ref(false)
const isMenuOpen = ref(false)
let lastScrollY = 0

// Calcul du drapeau
const currentFlag = computed(() => locale.value === 'fr' ? frFlag : enFlag)

// Directive personnalisée simple pour cliquer en dehors (click-outside)
const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = function(event) {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.body.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent);
  }
};

const toggleLangMenu = () => { isLangOpen.value = !isLangOpen.value }
const closeLangMenu = () => { isLangOpen.value = false }

const switchLang = (newLang) => {
  locale.value = newLang
  localStorage.setItem('user-locale', newLang)
  isLangOpen.value = false
  // On laisse le menu mobile ouvert pour voir le changement, ou on peut le fermer :
  // isMenuOpen.value = false 
}

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
  document.body.style.overflow = isMenuOpen.value ? 'hidden' : ''
}

const handleScroll = () => {
  const currentScrollY = window.scrollY
  isAtTop.value = currentScrollY < 50
  
  if (currentScrollY > lastScrollY && currentScrollY > 100) {
    isVisible.value = false
    isLangOpen.value = false // Ferme le dropdown si on scroll
  } else {
    isVisible.value = true
  }
  lastScrollY = currentScrollY
}

const scrollTo = (id) => {
  const element = document.getElementById(id)
  if (element) {
    // Offset ajusté à 50px pour la nouvelle hauteur
    const y = element.getBoundingClientRect().top + window.pageYOffset - 50
    window.scrollTo({ top: y, behavior: 'smooth' })
    
    isMenuOpen.value = false
    document.body.style.overflow = ''
  }
}

onMounted(() => window.addEventListener('scroll', handleScroll))
onUnmounted(() => window.removeEventListener('scroll', handleScroll))
</script>

<style scoped>
/* --- BASE NAVBAR --- */
.smart-navbar {
  position: fixed;
  top: 0; left: 0; width: 100%; z-index: 1000;
  transition: transform 0.3s ease, background 0.3s ease;
  background: rgba(250, 250, 250, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border-color);
  /* HAUTEUR RÉDUITE */
  height: 60px; 
}
.smart-navbar.hidden { transform: translateY(-100%); }
.smart-navbar.top { background: transparent; border-bottom: 1px solid transparent; }

.nav-content {
  max-width: 1000px;
  height: 100%;
  margin: 0 auto;
  /* Padding ajusté pour éviter que le contenu ne colle aux bords */
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-link { display: flex; align-items: center; }
.logo-img { width: 60px; height: 60px; object-fit: contain; margin:3px}

/* --- DESKTOP --- */
.nav-desktop {
  display: flex;
  align-items: center;
  gap: 30px;
}

.nav-links { display: flex; gap: 20px; }
.nav-links a {
  position: relative; /* Nécessaire pour positionner l'effet */
  font-family: var(--font-tech);
  font-size: 0.9rem;
  color: var(--text-secondary);
  text-decoration: none;
  font-weight: bold;
  cursor: pointer;
  transition: color 0.3s ease, transform 0.3s ease;
}

/* La ligne sous le lien (invisible par défaut) */
.nav-links a::after {
  content: '';
  position: absolute;
  bottom: -5px; /* Distance sous le texte */
  left: 50%;
  width: 0;
  height: 2px;
  background-color: var(--text-primary); /* Ou une couleur d'accent (ex: #000) */
  transition: all 0.3s ease-out;
  transform: translateX(-50%); /* On centre l'ancre */
}

/* État au survol */
.nav-links a:hover {
  color: var(--text-primary);
  transform: translateY(-2px); /* Léger mouvement vers le haut */
}

.nav-links a:hover::after {
  width: 100%; /* La ligne s'étend */
}

/* Dropdown Langue Desktop */
.lang-dropdown { position: relative; padding-left: 20px; border-left: 1px solid #ccc; height: 30px; display: flex; align-items: center; cursor: pointer; }
.lang-btn-current { background: transparent; border: none; cursor: pointer; display: flex; align-items: center; gap: 8px; padding: 0; }
.flag-icon { width: 24px; height: auto; border-radius: 2px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
.arrow-down { font-size: 0.7rem; color: #666; transition: transform 0.2s; }
.arrow-down.rotated { transform: rotate(180deg); }

.lang-menu {
  position: absolute;
  top: 100%; right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 5px 0;
  min-width: 130px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  display: flex; flex-direction: column; margin-top: 15px;
}

.lang-option {
  background: transparent; border: none; padding: 10px 15px;
  display: flex; align-items: center; gap: 10px; cursor: pointer;
  text-align: left; font-family: var(--font-tech); font-size: 0.85rem; color: #333; width: 100%;
}
.lang-option:hover { background: #f5f5f5; }
.lang-option.active { font-weight: bold; background: #eee; }

/* --- MOBILE BURGER --- */
.burger-btn {
  display: none; 
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 1001;
  margin-right: 2rem;
  padding: 2px; 
}

.burger-line {
  width: 100%; height: 2px; background-color: #000;
  transition: all 0.3s ease; transform-origin: left; display: block;
}

.burger-btn.open .burger-line:nth-child(1) { transform: rotate(45deg); }
.burger-btn.open .burger-line:nth-child(2) { opacity: 0; }
.burger-btn.open .burger-line:nth-child(3) { transform: rotate(-45deg); }

/* --- MOBILE MENU --- */
.mobile-menu {
  position: fixed;
  /* Ajusté à la nouvelle hauteur */
  top: 50px; 
  left: 0; width: 100%;
  height: calc(100vh - 50px);
  background: #fff;
  display: flex; flex-direction: column; align-items: center; justify-content: start;
}

.mobile-links { display: flex; flex-direction: column; gap: 30px; text-align: center; }
.mobile-links a { font-family: var(--font-tech); font-size: 1.5rem; color: #000; text-decoration: none; font-weight: 700; }

/* Langue Mobile */
.mobile-lang { margin-top: 20px; display: flex; flex-direction: column; align-items: center; gap: 20px; width: 100%; }
.lang-label { font-family: var(--font-tech); color: #666; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; }
.lang-options-mobile { display: flex; gap: 30px; }
.lang-options-mobile button {
  background: transparent; border: 1px solid #ddd; padding: 10px 25px; border-radius: 8px;
  cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 5px; transition: all 0.2s;
}
.lang-options-mobile button span { font-weight: bold; font-family: var(--font-tech); font-size: 0.9rem; }
.flag-icon-large { width: 40px; height: auto; border-radius: 4px; box-shadow: 0 2px 5px rgba(0,0,0,0.1); }
.lang-options-mobile button.active { background: #000; color: #fff; border-color: #000; transform: scale(1.05); }

/* --- RESPONSIVE CORRIGÉ --- */
@media (max-width: 768px) {
  /* On cache le menu desktop */
  .nav-desktop { display: none; }
  
  /* On affiche le burger */
  .burger-btn { display: flex; }

  /* Ajustements spécifiques mobile */
  .nav-content {
    /* Padding latéral de sécurité pour les écrans courbes ou étroits */
    padding: 0 15px; 
  }
}

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.slide-down-enter-active, .slide-down-leave-active { transition: transform 0.3s ease, opacity 0.3s ease; }
.slide-down-enter-from, .slide-down-leave-to { transform: translateY(-20px); opacity: 0; }
</style>